import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    OnInit,
    Output,
    inject,
} from '@angular/core'
import { FormBuilder, ReactiveFormsModule } from '@angular/forms'
import {
    Field,
    FieldEntities,
    FieldTypes,
    Workspace,
    LinkDefinition,
    ObjectType,
    Schema,
} from '@core/models'
import { FieldFacadeService, SchemaFacadeService } from '@core/services/store-facade'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { map, take } from 'rxjs/operators'
import { CellComponent } from '../../cell.component'
import { FieldNameComponent } from '../../../field-name/field-name.component'
import { MatOptionModule } from '@angular/material/core'

import { MatSelectModule } from '@angular/material/select'
import { MatFormFieldModule } from '@angular/material/form-field'
import { TranslocoModule } from '@jsverse/transloco'
import { FieldService } from '@services/entity-services/field/field.service'
import { combineLatest } from 'rxjs'

@UntilDestroy()
@Component({
    selector: 'app-link-settings',
    styleUrls: ['link-settings.component.sass'],
    templateUrl: './link-settings.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TranslocoModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        FieldNameComponent,
    ],
})
export class LinkSettingsComponent extends CellComponent implements OnInit {
    private readonly fb = inject(FormBuilder)
    private readonly schemaFacadeService = inject(SchemaFacadeService)
    private readonly fieldSearchService = inject(FieldService)
    private readonly fieldFacadeService = inject(FieldFacadeService)

    @Output()
    fieldChanged: EventEmitter<Field> = new EventEmitter<Field>()

    allSchema!: Schema[]
    fieldEntities!: Record<string, FieldEntities>
    schemasFields!: Record<string, Field[]>
    workspace!: Workspace

    linkDefinition = this.fb.group({
        schema: '',
        fields: { value: [''], disabled: true },
    })

    ngOnInit() {
        combineLatest([
            this.schemaFacadeService.selectAllSchemas$,
            this.fieldFacadeService.selectFieldEntitiesBySchema$,
        ])
            .pipe(
                take(1),
                untilDestroyed(this),
                map(([schemas, entities]) => this.filterSchemasAndFields(schemas, entities)),
            )
            .subscribe(([schemas, fields, fieldEntities]) => {
                this.allSchema = schemas
                this.schemasFields = fields
                this.fieldEntities = fieldEntities

                if (this.field.link_definition) {
                    this.setDefaultLinDefinition(this.field.link_definition)
                }
            })

        this.linkDefinition.controls.schema.valueChanges.subscribe((value) => {
            if (!!value) {
                this.linkDefinition.controls.fields.enable()
            } else {
                this.linkDefinition.controls.fields.disable()
            }
        })
    }

    get selectedSchemaFields(): Field[] {
        const guid = this.linkDefinition.controls.schema.value

        return guid ? this.schemasFields[guid] : []
    }

    get fieldsValue(): string[] {
        return this.linkDefinition.controls.fields.value || []
    }

    getField(fieldGuid: string): Field {
        return this.selectedSchemaFields.find((schemaField) => schemaField.guid === fieldGuid)!
    }

    updateValue(isOpened: boolean) {
        if (isOpened) return

        const { schema: schemaGuid, fields: formFields } = this.linkDefinition.getRawValue()
        if (!schemaGuid || !formFields) return

        const fields = this.fieldEntities[schemaGuid]
        if (!fields) return

        const nameField = this.fieldSearchService.findImportantFieldByType(fields, FieldTypes.NAME)
        if (!nameField) return

        this.fieldChanged.emit({
            ...this.field,
            link_definition: {
                target_object_field_guids: [nameField.guid, ...formFields],
                target_solution_object_type_guid: schemaGuid,
            },
        })
    }

    private setDefaultLinDefinition(linkDefinition: LinkDefinition) {
        const schemaGuid = linkDefinition.target_solution_object_type_guid
        const fields = this.fieldEntities[schemaGuid]
        if (!fields) return

        const nameField = this.fieldSearchService.findImportantFieldByType(fields, FieldTypes.NAME)

        if (!nameField) return
        const linkFieldsWithoutNameField = linkDefinition.target_object_field_guids.filter(
            (guid) => guid !== nameField.guid,
        )

        this.linkDefinition.setValue({
            schema: schemaGuid,
            fields: linkFieldsWithoutNameField,
        })
        this.linkDefinition.controls.schema.disable()
        this.linkDefinition.controls.fields.enable()
    }

    private filterSchemasAndFields(schemas: Schema[], entities: Record<string, FieldEntities>) {
        const filteredSchemas = schemas.filter((schema) => {
            return schema.system_object_type_code === ObjectType.TABLE
        })

        const filters = filteredSchemas.reduce(
            (acc, schema) => {
                const guid = schema.guid
                return { ...acc, [guid]: this.filterFields(this.fieldEntities[guid]) }
            },
            {} as Record<string, Field[]>,
        )

        return [filteredSchemas, filters, entities] as [
            Schema[],
            Record<string, Field[]>,
            Record<string, FieldEntities>,
        ]
    }

    private filterFields(fields: FieldEntities) {
        const filteredFieldTypes: string[] = [FieldTypes.LINK, FieldTypes.NAME]

        return Object.values(fields).filter(
            (filter) => !filteredFieldTypes.includes(filter.field_type_code),
        )
    }
}
